import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useData } from "../../../context/DataContext";
import Chart from 'react-apexcharts';
import moment from "moment";
import * as XLSX from 'xlsx';
import {
    Box, IconButton, Paper, Card, Grid, CardContent, Divider, Button, Stack, TextField, Typography, Accordion,
    AccordionSummary,
    AccordionDetails, Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CustomButton from "../../../components/buttons/customButton";
import HistoryIcon from "@mui/icons-material/History";
import CommentIcon from "@mui/icons-material/Comment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { getClientTheme } from "../../../theme/clientTheme";
import LoadingComponent from "../../../components/Loading/LoadingComponent";


const TrainingSessionPlanHistoryDetails = () => {
    const scrollRef = useRef(null);
    const sessionHistoryValues = [
        { value: 1, label: "Plan History", icon: <HistoryIcon /> },
        { value: 2, label: "Session Feedback", icon: <CommentIcon /> },
        { value: 3, label: "Session Attendance", icon: <EventAvailableIcon /> },
    ];


    const { sessionAttendance = [], user_client_id ,subLoading} = useData();
    const clientTheme = getClientTheme(user_client_id);


    const calculateAttendanceCounts = (data) => {
        const counts = { 0: 0, 1: 0, 2: 0 };
        data.forEach(item => {
            counts[item.attendance_status] += 1;
        });
        return counts;
    };

    const attendanceCounts = calculateAttendanceCounts(sessionAttendance);
    const employeesCount = [attendanceCounts[0],
    attendanceCounts[1],
        //   attendanceCounts[2]
    ];


    const baseColors = ['#FF4560', '#00E396'];
    const dynamicColors = employeesCount.map((_, idx) => baseColors[idx % baseColors.length]);

    const chartOptions = {
        chart: { type: 'bar', stacked: false, toolbar: { show: false } },
        colors: dynamicColors,
        xaxis: {
            categories: ['Absent', 'Present'],
            labels: { show: false },
        },
        yaxis: {
            labels: {
                formatter: function (val) {
                    return val.toFixed(0);
                },
                show: true,
            },
            min: 0,
        },
        stroke: { curve: 'smooth', width: 1 },
        markers: { size: 1 },
        plotOptions: {
            bar: {
                columnWidth: '35%',
                distributed: true,
            }
        },
        dataLabels: { enabled: true }
    };

    const chartSeries = [{
        name: 'Count',
        data: employeesCount
    }];


    const navigate = useNavigate();
    const location = useLocation();
    const { rowData } = location.state || {};

    const [state, setState] = useState({
        selectedOption: 1,
        selectedDateWiseFeedBackData: [],
        sessionAttendanceResults: [],
    });


    const handleOptionChange = (value) => {
        setState(prevState => ({
            ...prevState,
            selectedOption: value,
            selectedDateWiseFeedBackData: [rowData]
        }));
    };


    const trainingPlanColumns = [
        { label: "Training Plan Name", accessor: rowData.training_content ? rowData.training_content : "-", type: "textarea" },
        { label: "Level Name", accessor: rowData.level_name ? rowData.level_name : "-", type: "textarea" },
        { label: "Training Topics", accessor: rowData.training_topics ? rowData.training_topics : "-", type: "textarea" },
        { label: "Target Group", accessor: rowData.target_group ? rowData.target_group : "-", type: "textarea" },
        { label: "resource_person", accessor: rowData.resource_person ? rowData.resource_person : "-", type: "textarea" },
        { label: "training_methodology", accessor: rowData.training_methodology ? rowData.training_methodology : "-", type: "textarea" },
        { label: "comments", accessor: rowData.comments ? rowData.comments : "-", type: "textarea" },
        { label: "Year", accessor: rowData.year_name ? rowData.year_name : "-", type: "textarea" },
        { label: "Village Name", accessor: rowData.village_names ? rowData.village_names : "-", type: "textarea" },
        { label: "Training Status", accessor: rowData.training_status ? rowData.training_status : "-", type: "textarea" },
    ];

    const handleDownload = (item) => {
        downloadFile(item);
    };

    const downloadFile = (item) => {
        fetch(item.fileUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.blob();
            })
            .then(blob => {
                const fileURL = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = fileURL;
                a.download = item.fileName;
                document.body.appendChild(a);
                a.click();
                URL.revokeObjectURL(fileURL);
                document.body.removeChild(a);
            })
            .catch(error => {
                console.error('Error downloading file:', error);
            });
    };

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            selectedDateWiseFeedBackData: [rowData]
        }));
    }, [rowData]);



    useEffect(() => {
        const summary = sessionAttendance
            .filter((item) => item.attended_date && !isNaN(item.attended_date))
            .reduce((acc, item) => {
                const status = item.attendance_status;

                const date = formatDateToDDMMYYYY(item.attended_date);

                if (!acc[date]) {
                    acc[date] = {
                        date,
                        present: 0,
                        absent: 0,
                        notMarked: 0,
                    };
                }

                if (status === 1) {
                    acc[date].present += 1;
                } else if (status === 0) {
                    acc[date].absent += 1;
                } else {
                    acc[date].notMarked += 1;
                }
                return acc;
            }, {});

        setState(prevState => ({
            ...prevState,
            sessionAttendanceResults: Object.values(summary)
        }))
    }, [sessionAttendance]);


    function formatDateToDDMMYYYY(timestamp) {
        if (!timestamp || isNaN(timestamp)) {
            return 'Invalid Date';
        }
        const date = new Date(parseInt(timestamp, 10) * 1000);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    }



    const handleExcelDownload = (type, date) => {
        const formatAttendanceData = (data) =>
            data.map((item, index) => ({
                sno: index + 1,
                farmer_name: item.farmer_name !== null ? item.farmer_name : "-",
                attendance_status:
                    item.attendance_status !== null
                        ? item.attendance_status === 0
                            ? "Absent"
                            : item.attendance_status === 1
                                ? "Present"
                                : "Not Marked"
                        : "-",
                attended_date: item.attended_date
                    ? moment.unix(item.attended_date).format("DD/MM/YYYY")
                    : "-",
            }));


        let filteredData = sessionAttendance.filter((item) =>
            moment.unix(item.attended_date).format("DD-MM-YYYY") === date
        );

        let farmerAttendanceDetails = []
        if (type === "present") {
            farmerAttendanceDetails = filteredData.filter(
                (item) => item.attendance_status === 1
            );
        } else if (type === "absent") {
            farmerAttendanceDetails = filteredData.filter(
                (item) => item.attendance_status === 0
            );
        }


        const formattedData = formatAttendanceData(farmerAttendanceDetails);
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, "AttendanceReports.xlsx");
    };


    if (subLoading) {
        return <LoadingComponent />;
    }


    return (
        <Box>
            <Card elevation={6}>

                <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    p={2}
                >
                    <CustomButton
                        label="Back"
                        status={false}
                        onClick={() => navigate(-1)}
                        icon={<ArrowBackIcon />}
                    />
                </Box>

                <Divider sx={{ my: 0, bgcolor: '#f5f1f1ff' }} />

                <Box sx={{ p: 2 }}>
                    <Grid container spacing={1}>
                        <Box ref={scrollRef}
                            sx={{
                                overflowX: "auto",
                                whiteSpace: "nowrap",
                                scrollBehavior: "smooth",
                                width: "100%",
                                px: 1, mb: 2,
                                "&::-webkit-scrollbar": { display: "none" },
                            }}>
                            <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                                {sessionHistoryValues.map((item, index) => (
                                    <Box key={index} sx={{ display: "inline-block", textAlign: "center" }}>
                                        <Button
                                            key={item.value}
                                            startIcon={item.icon}
                                            variant={state.selectedOption === item.value ? "contained" : "outlined"}
                                            color="success"
                                            onClick={() => handleOptionChange(item.value)}
                                            sx={{
                                                textTransform: "none", borderRadius: 2,
                                                "&:hover": {
                                                    backgroundColor: state.selectedOption === item.value
                                                        ? "success.dark"
                                                        : "success.main",
                                                    color: "#fff",
                                                    borderColor: "success.main",
                                                },
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                </Box>

                <CardContent>

                    {
                        state.selectedOption === 1 ? (
                            <Grid container spacing={1}>
                                {trainingPlanColumns.map((item, index) => (
                                    <Grid item size={{ xs: 12, md: 6 }} key={index}>
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                fontWeight="bold"
                                                sx={{ textTransform: "uppercase", mb: 0.5, display: "block" }}
                                            >
                                                {item.label}
                                            </Typography>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={item.accessor}
                                                disabled
                                                multiline={item.type === "textarea"}
                                                rows={3}
                                                sx={{
                                                    "& .MuiInputBase-input": {
                                                        color: "#000",        // value text color
                                                    },
                                                    "& .Mui-disabled .MuiInputBase-input": {
                                                        WebkitTextFillColor: "#000",   // important for disabled
                                                    },
                                                    // background for disabled
                                                    "& .MuiOutlinedInput-root.Mui-disabled": {
                                                        backgroundColor: "#e5e5e5",      // change to what you want
                                                    },
                                                    "& .MuiInputLabel-root": {
                                                        color: "#000",
                                                        fontWeight: 700, // thick black
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : state.selectedOption === 2 ? (
                            <>
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center", textTransform: 'uppercase'
                                }}>
                                    <Typography variant="h5">Feedback History</Typography>
                                </Box>

                                {state.selectedDateWiseFeedBackData.map((item, index) => {
                                    // ---- SAFE JSON PARSE FOR FEEDBACK ----
                                    const feedbackArray =
                                        typeof item.feedback === "string"
                                            ? JSON.parse(item.feedback)
                                            : Array.isArray(item.feedback)
                                                ? item.feedback
                                                : [];

                                    const feedbackComments =
                                        feedbackArray.length > 0
                                            ? feedbackArray
                                                .map((f) => (f.feedBack || "").replace(/\n/g, "<br/>"))
                                                .join(", ")
                                            : "Feedback cannot be uploaded.";

                                    // ---- SAFE JSON PARSE FOR IMAGES ----
                                    const photosArray =
                                        typeof item.training_session_photos === "string"
                                            ? JSON.parse(item.training_session_photos)
                                            : Array.isArray(item.training_session_photos)
                                                ? item.training_session_photos
                                                : [];

                                    const trainingSessionPhotos = photosArray.map((photo) => ({
                                        fileUrl: photo.file,
                                        fileName: photo.fileName || "",
                                        uploadTime: photo.uploadTime,
                                    }));

                                    // ---- GROUP BY DATE ----
                                    const groupedData = trainingSessionPhotos.reduce((acc, img) => {
                                        const formattedDate = moment
                                            .unix(img.uploadTime)
                                            .format("DD/MM/YYYY");
                                        acc[formattedDate] = acc[formattedDate] || [];
                                        acc[formattedDate].push(img);
                                        return acc;
                                    }, {});

                                    if (trainingSessionPhotos.length === 0) {
                                        return (
                                            <Typography key={index} fontWeight="bold" textAlign="center">
                                                No Data Found
                                            </Typography>
                                        );
                                    }

                                    return Object.entries(groupedData).map(([date, images], dateIndex) => (
                                        <Accordion key={`${index}-${dateIndex}`} sx={{ mt: 2, border: "1px solid green" }} elevation={6}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography fontWeight="bold">{date}</Typography>
                                            </AccordionSummary>

                                            <AccordionDetails>
                                                {/* COMMENTS */}
                                                <Box textAlign="center" mb={2}>
                                                    <Typography component="span" fontWeight="bold">
                                                        Comments:
                                                    </Typography>
                                                    <Box sx={{ wordBreak: "break-all" }}
                                                        dangerouslySetInnerHTML={{ __html: feedbackComments }}
                                                    />
                                                </Box>

                                                {/* IMAGES */}
                                                <Grid container spacing={2}>
                                                    {images.map((image, imageIndex) => (
                                                        <Grid item xs={12} sm={4} key={imageIndex}>
                                                            <Box textAlign="center">
                                                                <Box
                                                                    component="img"
                                                                    src={image.fileUrl}
                                                                    alt={image.fileName}
                                                                    sx={{
                                                                        height: 200,
                                                                        width: "100%",
                                                                        objectFit: "cover",
                                                                        borderRadius: 1,
                                                                    }}
                                                                />

                                                                <Box
                                                                    mt={1}
                                                                    display="flex"
                                                                    justifyContent="center"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                >
                                                                    <Typography variant="body2">
                                                                        {image.fileName}
                                                                    </Typography>
                                                                    <IconButton
                                                                        color="success"
                                                                        size="small"
                                                                        onClick={() => handleDownload(image)}
                                                                    >
                                                                        <DownloadIcon />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    ));
                                })}
                            </>
                        ) : (
                            <>
                                {
                                    rowData.training_type_id === 2 ? (
                                        <Box mt={5} textAlign="center">
                                            <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
                                                Attendee Count:{" "}
                                                <span>{rowData?.attended_count ?? "-"}</span>
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {state.sessionAttendanceResults.length === 0 ? (
                                                <Grid item size={{ xs: 12, md: 12 }}>
                                                    <Typography
                                                        color="success.main"
                                                        fontWeight="bold"
                                                        textAlign="center"
                                                        textTransform="uppercase"
                                                        mt={5}
                                                    >
                                                        No valid attendance data available.
                                                    </Typography>
                                                </Grid>
                                            ) : (
                                                <>
                                                    {/* CHART */}
                                                    <Grid item size={{ xs: 12, md: 4 }}>
                                                        <Card elevation={6}>
                                                            <CardContent>
                                                                <Chart
                                                                    options={chartOptions}
                                                                    series={chartSeries}
                                                                    type="bar"
                                                                    height={300}
                                                                />
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>

                                                    {/* TABLE */}
                                                    <Grid item size={{ xs: 12, md: 8 }}>

                                                        <TableContainer component={Paper} sx={{
                                                            boxShadow: 6,
                                                            maxHeight: "60vh",
                                                            overflowY: "auto",          // vertical scroll
                                                            overflowX: { xs: "auto", md: "hidden" }, // horizontal scroll only on mobile
                                                            WebkitOverflowScrolling: "touch", // smooth mobile scroll
                                                        }}>
                                                            <Table size="small">
                                                                <TableHead sx={{
                                                                    backgroundColor: clientTheme.activeBg,
                                                                }}>
                                                                    <TableRow>
                                                                        <TableCell sx={{ color: "#fff", p: 1.5, textAlign: "center" }}>S.No</TableCell>
                                                                        <TableCell sx={{ color: "#fff", p: 1.5, textAlign: "center" }}>Attended Date</TableCell>
                                                                        <TableCell sx={{ color: "#fff", p: 1.5, textAlign: "center" }}>Present</TableCell>
                                                                        <TableCell sx={{ color: "#fff", p: 1.5, textAlign: "center" }}>Absent</TableCell>
                                                                        <TableCell sx={{ color: "#fff", p: 1.5, textAlign: "center" }}>Download</TableCell>
                                                                    </TableRow>
                                                                </TableHead>

                                                                <TableBody>
                                                                    {state.sessionAttendanceResults.map((item, index) => {
                                                                        const isDisabled =
                                                                            item.present === 0 && item.absent === 0;

                                                                        return (
                                                                            <TableRow key={index}>
                                                                                <TableCell align="center">{index + 1}</TableCell>
                                                                                <TableCell align="center">{item.date}</TableCell>
                                                                                <TableCell align="center">{item.present}</TableCell>
                                                                                <TableCell align="center">{item.absent}</TableCell>
                                                                                <TableCell align="center">
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="contained"
                                                                                        color="success"
                                                                                        startIcon={<DownloadIcon />}
                                                                                        sx={{ mr: 1 }}
                                                                                        disabled={isDisabled || item.present === 0}
                                                                                        onClick={() =>
                                                                                            handleExcelDownload("present", item.date)
                                                                                        }
                                                                                    >
                                                                                        Present
                                                                                    </Button>

                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="contained"
                                                                                        color="error"
                                                                                        startIcon={<DownloadIcon />}
                                                                                        disabled={isDisabled || item.absent === 0}
                                                                                        onClick={() =>
                                                                                            handleExcelDownload("absent", item.date)
                                                                                        }
                                                                                    >
                                                                                        Absent
                                                                                    </Button>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>

                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    )
                                }
                            </>
                        )
                    }
                </CardContent>
            </Card>
        </Box>
    );
};
export default TrainingSessionPlanHistoryDetails;